import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { FormsModule } from '@angular/forms';

import { PageLayoutModule } from '../../global/page-layout/page-layout.module';
import { SimulatedTimeService } from '../../global/simulated-time/simulated-time.service';

/**
 * Admin control for winding the app's clock, so an event weeks away can be inspected
 * as it will look on the day. Affects this browser only -- see SimulatedTimeService.
 */
@Component({
    standalone: true,
    selector: 'abs-simulated-time-page',
    imports: [CommonModule, IonicModule, FormsModule, PageLayoutModule],
    template: `
        <abs-page-layout pageTitle="Simulated Time" defaultHref="/admin">
            <p>
                Set the date and time the app should believe it is. Anything that renders based on the clock — the event day schedule's
                current-time line, the "round starting soon" prompt — will behave as if that were now.
            </p>
            <p>
                This applies to <strong>this browser only</strong> and changes nothing on the server or for anyone else. It persists until
                you clear it, including across reloads.
            </p>

            @if (time.isSimulated()) {
                <ion-item lines="none" color="warning">
                    <ion-label>
                        <h3>Simulation active</h3>
                        <p>{{ time.now() | date: 'full' }}</p>
                    </ion-label>
                </ion-item>
            }

            <ion-item>
                <ion-input
                    type="datetime-local"
                    label="Simulated date and time"
                    labelPlacement="stacked"
                    [(ngModel)]="draft"
                ></ion-input>
            </ion-item>

            <div class="actions">
                <ion-button (click)="apply()" [disabled]="!draft">Apply</ion-button>
                <ion-button color="medium" (click)="clear()" [disabled]="!time.isSimulated()">Use Real Time</ion-button>
            </div>
        </abs-page-layout>
    `,
    styles: [
        `
            .actions {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
                margin-top: 16px;
            }
        `
    ]
})
export class SimulatedTimePage {
    readonly time = inject(SimulatedTimeService);

    /** datetime-local wants `YYYY-MM-DDTHH:mm` in LOCAL time, not an ISO UTC string. */
    draft = toLocalInputValue(this.time.override());

    private readonly touched = signal(false);

    apply() {
        if (!this.draft) return;
        // Parsed as local time, which is what the organiser typed and what the schedule's
        // block times are compared against.
        this.time.setOverride(new Date(this.draft));
        this.touched.set(true);
    }

    clear() {
        this.time.clear();
        this.draft = '';
    }
}

function toLocalInputValue(date: Date | null): string {
    if (!date) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
